import {
  ROOT_STUDIO_WORKSPACE_ANCHOR,
  type AgentWorkspaceAnchor,
} from "./anchors/anchor-model";
import type {
  AgentWorkspaceIntentKind,
  AgentWorkspaceRequirement,
} from "./comments/requirement-model";
import {
  buildStudioChartIntegrationPlan,
  buildStudioDocumentSurfacePolicy,
  type DocumentSurfaceStatus,
  type StudioChartIntegrationPlan,
  type StudioDocumentSurfacePolicy,
} from "./external-surfaces/document-surface-policy";

export const STUDIO_AGENT_WORKSPACE_MODEL_SCHEMA_VERSION =
  "cogo.studio-agent-workspace-model.v1" as const;

export type AgentWorkspaceModelStatus = DocumentSurfaceStatus;

export type AgentWorkspaceCollaboratorId =
  | "cogo-agent"
  | "codex-deterministic"
  | "qwen-reviewer"
  | "gemma-reviewer"
  | "user";

export type AgentWorkspaceCollaboratorKind =
  | "human"
  | "orchestrator"
  | "deterministic-gate"
  | "model-reviewer";

export interface AgentWorkspaceCollaborator {
  id: AgentWorkspaceCollaboratorId;
  label: string;
  kind: AgentWorkspaceCollaboratorKind;
  status: AgentWorkspaceModelStatus;
  authority:
    | "user-intent"
    | "workflow-orchestrator"
    | "deterministic-proof"
    | "advisory-model-review";
  mayPatchCogoJsonDirectly: boolean;
  responsibilities: string[];
  evidenceRefs: string[];
}

export interface AgentWorkspaceEvidenceLane {
  id:
    | "preview-anchor"
    | "source-model"
    | "qwen-gemma-review"
    | "semantic-patch"
    | "ontology-generated-application-interface"
    | "export-proof"
    | "chart-integration"
    | "report-production-input"
    | "report-export-targets"
    | "report-formula-oracle"
    | "report-chart-semantics"
    | "report-publish-readiness";
  label: string;
  status: AgentWorkspaceModelStatus;
  owner: AgentWorkspaceCollaboratorId;
  evidenceRefs: string[];
}

export interface AgentWorkspaceReportTargetSummary {
  format: string;
  status: AgentWorkspaceModelStatus;
  oracle: string;
}

export interface AgentWorkspaceReportBridge {
  schemaVersion: "cogo.studio-agent-workspace-report-bridge.v1";
  status: AgentWorkspaceModelStatus;
  title: string;
  sourceKind: string;
  readyTargetCount: number;
  targetCount: number;
  productionInputStatus: AgentWorkspaceModelStatus;
  exportGateStatus: AgentWorkspaceModelStatus;
  nativeExportReady: boolean;
  formulaOracleStatus: AgentWorkspaceModelStatus;
  chartSemanticStatus: AgentWorkspaceModelStatus;
  publishBlocked: boolean;
  targets: AgentWorkspaceReportTargetSummary[];
  blockers: string[];
  evidenceRefs: string[];
}

export interface AgentWorkspaceOntologyGeneratedApplicationBridge {
  schemaVersion: "cogo.studio-agent-workspace-ontology-generated-application-bridge.v1";
  status: AgentWorkspaceModelStatus;
  appId: string;
  title: string;
  generationMode: string;
  finalArtifactWriter: string;
  promotionAuthority: string;
  modelRoles: Record<string, string>;
  deterministicCompilerHash: string;
  screenCount: number;
  componentCount: number;
  actionFlowCount: number;
  surfaceCount: number;
  flowStateCount: number;
  actionCount: number;
  screenshotCount: number;
  requiredCheckCount: number;
  passingCheckCount: number;
  blockedClaims: string[];
  previewRef: string;
  applicationJsonRef: string;
  generationProofRef: string;
  sourcePromotionRef: string;
  modelOrchestrationRef: string;
  mayPatchCogoJsonDirectly: boolean;
  publishBlocked: boolean;
  blockers: string[];
}

export interface AgentWorkspaceActiveTask {
  taskId: string;
  label: string;
  intent: AgentWorkspaceIntentKind;
  status: AgentWorkspaceModelStatus;
  owner: AgentWorkspaceCollaboratorId;
  anchorId: string;
  requirementId?: string;
  semanticPatchId?: string;
  validationPlan: string[];
  evidenceRefs: string[];
  mayPatchCogoJsonDirectly: boolean;
}

export interface StudioAgentWorkspaceModel {
  schemaVersion: typeof STUDIO_AGENT_WORKSPACE_MODEL_SCHEMA_VERSION;
  status: AgentWorkspaceModelStatus;
  title: string;
  activeAnchor: AgentWorkspaceAnchor;
  activeRequirement?: AgentWorkspaceRequirement;
  activeTask?: AgentWorkspaceActiveTask;
  collaborators: AgentWorkspaceCollaborator[];
  evidenceLanes: AgentWorkspaceEvidenceLane[];
  documentSurfacePolicy: StudioDocumentSurfacePolicy;
  chartIntegrationPlan: StudioChartIntegrationPlan;
  reportWorkspace?: AgentWorkspaceReportBridge;
  ontologyGeneratedApplication?: AgentWorkspaceOntologyGeneratedApplicationBridge;
  blockers: string[];
}

export interface StudioAgentWorkspaceModelInput {
  title?: string;
  activeAnchor?: AgentWorkspaceAnchor;
  qwenStatus?: AgentWorkspaceModelStatus;
  gemmaStatus?: AgentWorkspaceModelStatus;
  deterministicStatus?: AgentWorkspaceModelStatus;
  previewAnchorStatus?: AgentWorkspaceModelStatus;
  sourceModelStatus?: AgentWorkspaceModelStatus;
  semanticPatchStatus?: AgentWorkspaceModelStatus;
  exportProofStatus?: AgentWorkspaceModelStatus;
  wordgardReviewed?: boolean;
  chartSidecarReady?: boolean;
  univerFormulaOracleReady?: boolean;
  univerChartAdapterReady?: boolean;
  reportWorkspaceChartInspectorReady?: boolean;
  exportProofLinked?: boolean;
  reportWorkspace?: AgentWorkspaceReportBridge | null;
  ontologyGeneratedApplication?: AgentWorkspaceOntologyGeneratedApplicationBridge | null;
  activeRequirement?: AgentWorkspaceRequirement | null;
  activeTask?: AgentWorkspaceActiveTask | null;
}

function worstStatus(statuses: AgentWorkspaceModelStatus[]): AgentWorkspaceModelStatus {
  if (statuses.includes("blocked")) return "blocked";
  if (statuses.includes("missing")) return "missing";
  if (statuses.includes("warn") || statuses.includes("planned")) return "warn";
  return "ready";
}

function buildCollaborators(
  input: StudioAgentWorkspaceModelInput,
): AgentWorkspaceCollaborator[] {
  return [
    {
      id: "user",
      label: "User",
      kind: "human",
      status: "ready",
      authority: "user-intent",
      mayPatchCogoJsonDirectly: false,
      responsibilities: ["request", "review", "acceptance decision"],
      evidenceRefs: [],
    },
    {
      id: "cogo-agent",
      label: "Coogo Agent",
      kind: "orchestrator",
      status: "warn",
      authority: "workflow-orchestrator",
      mayPatchCogoJsonDirectly: false,
      responsibilities: [
        "intent classification",
        "anchor inspection",
        "semantic patch proposal",
      ],
      evidenceRefs: ["docs/studio/cogo-agent-report-workspace-2026-07-04.md"],
    },
    {
      id: "codex-deterministic",
      label: "Codex deterministic gates",
      kind: "deterministic-gate",
      status: input.deterministicStatus ?? "warn",
      authority: "deterministic-proof",
      mayPatchCogoJsonDirectly: false,
      responsibilities: [
        "schema validation",
        "source fidelity verification evidence",
        "export/readback verification evidence",
      ],
      evidenceRefs: [
        "apps/cogo-studio/src/shell/ide-v2/surfaces/evidence-surface.tsx",
      ],
    },
    {
      id: "qwen-reviewer",
      label: "Qwen semantic review",
      kind: "model-reviewer",
      status: input.qwenStatus ?? "missing",
      authority: "advisory-model-review",
      mayPatchCogoJsonDirectly: false,
      responsibilities: ["semantic review evidence", "source relation finding"],
      evidenceRefs: [
        "apps/cogo-studio/src/shell/ide-v2/surfaces/evidence-surface.tsx",
      ],
    },
    {
      id: "gemma-reviewer",
      label: "Gemma visual review",
      kind: "model-reviewer",
      status: input.gemmaStatus ?? "missing",
      authority: "advisory-model-review",
      mayPatchCogoJsonDirectly: false,
      responsibilities: ["visual review evidence", "source-boundary finding"],
      evidenceRefs: [
        "apps/cogo-studio/src/shell/ide-v2/surfaces/evidence-surface.tsx",
      ],
    },
  ];
}

function buildEvidenceLanes(
  input: StudioAgentWorkspaceModelInput,
): AgentWorkspaceEvidenceLane[] {
  return [
    {
      id: "preview-anchor",
      label: "Preview anchor",
      status: input.previewAnchorStatus ?? "warn",
      owner: "cogo-agent",
      evidenceRefs: ["apps/cogo-studio/src/features/preview/selection"],
    },
    {
      id: "source-model",
      label: "Source model",
      status: input.sourceModelStatus ?? "warn",
      owner: "codex-deterministic",
      evidenceRefs: ["packages/cogo-schema/v4"],
    },
    {
      id: "qwen-gemma-review",
      label: "Model review evidence",
      status: worstStatus([
        input.qwenStatus ?? "missing",
        input.gemmaStatus ?? "missing",
      ]),
      owner: "qwen-reviewer",
      evidenceRefs: [
        "apps/cogo-studio/src/shell/ide-v2/surfaces/evidence-surface.tsx",
      ],
    },
    {
      id: "semantic-patch",
      label: "Semantic patch",
      status: input.semanticPatchStatus ?? "warn",
      owner: "cogo-agent",
      evidenceRefs: ["docs/studio/cogo-agent-report-workspace-2026-07-04.md"],
    },
    ...(input.ontologyGeneratedApplication
      ? [
          {
            id: "ontology-generated-application-interface" as const,
            label: "Ontology application interface",
            status: input.ontologyGeneratedApplication.status,
            owner: "codex-deterministic" as const,
            evidenceRefs: [
              input.ontologyGeneratedApplication.applicationJsonRef,
              input.ontologyGeneratedApplication.generationProofRef,
              input.ontologyGeneratedApplication.sourcePromotionRef,
              input.ontologyGeneratedApplication.modelOrchestrationRef,
            ],
          },
        ]
      : []),
    {
      id: "export-proof",
      label: "Export verification evidence",
      status: input.exportProofStatus ?? "warn",
      owner: "codex-deterministic",
      evidenceRefs: [
        "apps/cogo-studio/src/features/report-workspace/report-workspace-model.ts",
      ],
    },
    {
      id: "chart-integration",
      label: "Chart integration",
      status: input.chartSidecarReady ? "warn" : "missing",
      owner: "codex-deterministic",
      evidenceRefs: [
        "docs/studio/wordgard-univer-chart-integration-plan-2026-07-05.md",
      ],
    },
    ...(input.reportWorkspace
      ? [
          {
            id: "report-production-input" as const,
            label: "Report production input",
            status: input.reportWorkspace.productionInputStatus,
            owner: "codex-deterministic" as const,
            evidenceRefs: input.reportWorkspace.evidenceRefs,
          },
          {
            id: "report-export-targets" as const,
            label: "Report export targets",
            status: input.reportWorkspace.status,
            owner: "codex-deterministic" as const,
            evidenceRefs: input.reportWorkspace.evidenceRefs,
          },
          {
            id: "report-formula-oracle" as const,
            label: "Report formula oracle",
            status: input.reportWorkspace.formulaOracleStatus,
            owner: "codex-deterministic" as const,
            evidenceRefs: input.reportWorkspace.evidenceRefs,
          },
          {
            id: "report-chart-semantics" as const,
            label: "Report chart semantics",
            status: input.reportWorkspace.chartSemanticStatus,
            owner: "codex-deterministic" as const,
            evidenceRefs: input.reportWorkspace.evidenceRefs,
          },
          {
            id: "report-publish-readiness" as const,
            label: "Report publish readiness",
            status: input.reportWorkspace.publishBlocked
              ? ("blocked" as const)
              : ("ready" as const),
            owner: "codex-deterministic" as const,
            evidenceRefs: input.reportWorkspace.evidenceRefs,
          },
        ]
      : []),
  ];
}

function statusFromRequirement(
  requirement: AgentWorkspaceRequirement,
): AgentWorkspaceModelStatus {
  if (requirement.status === "blocked") return "blocked";
  if (
    requirement.status === "patched" ||
    requirement.status === "validated" ||
    requirement.status === "resolved"
  ) {
    return "ready";
  }
  return "warn";
}

export function createAgentWorkspaceActiveTaskFromRequirement(
  requirement: AgentWorkspaceRequirement,
  anchor: AgentWorkspaceAnchor,
  input: {
    taskId?: string;
    semanticPatchId?: string;
    evidenceRefs?: string[];
    validationPlan?: string[];
  } = {},
): AgentWorkspaceActiveTask {
  const validationPlan =
    input.validationPlan && input.validationPlan.length > 0
      ? input.validationPlan
      : requirement.acceptanceCriteria;
  return {
    taskId: input.taskId ?? `${requirement.requirementId}.task`,
    label: requirement.body,
    intent: requirement.intent,
    status: statusFromRequirement(requirement),
    owner: "cogo-agent",
    anchorId: anchor.anchorId,
    requirementId: requirement.requirementId,
    semanticPatchId: input.semanticPatchId,
    validationPlan,
    evidenceRefs: [
      ...anchor.evidenceRefs,
      ...requirement.proofRefs,
      ...(input.evidenceRefs ?? []),
    ],
    mayPatchCogoJsonDirectly: false,
  };
}

export function buildStudioAgentWorkspaceModel(
  input: StudioAgentWorkspaceModelInput = {},
): StudioAgentWorkspaceModel {
  const documentSurfacePolicy = buildStudioDocumentSurfacePolicy({
    wordgardReviewed: input.wordgardReviewed,
    chartSidecarReady: input.chartSidecarReady,
    univerFormulaOracleReady: input.univerFormulaOracleReady,
  });
  const chartIntegrationPlan = buildStudioChartIntegrationPlan({
    chartSidecarReady: input.chartSidecarReady,
    univerChartAdapterReady: input.univerChartAdapterReady,
    reportWorkspaceChartInspectorReady:
      input.reportWorkspaceChartInspectorReady,
    exportProofLinked: input.exportProofLinked,
  });
  const activeAnchor = input.activeAnchor ?? ROOT_STUDIO_WORKSPACE_ANCHOR;
  const activeRequirement = input.activeRequirement ?? undefined;
  const activeTask =
    input.activeTask ??
    (activeRequirement
      ? createAgentWorkspaceActiveTaskFromRequirement(
          activeRequirement,
          activeAnchor,
        )
      : undefined);
  const collaborators = buildCollaborators(input);
  const evidenceLanes = buildEvidenceLanes(input);
  const blockers = [
    ...documentSurfacePolicy.blockers,
    ...chartIntegrationPlan.blockers,
    ...evidenceLanes.flatMap((lane) =>
      lane.status === "blocked" || lane.status === "missing"
        ? [`${lane.id}: ${lane.label}`]
        : [],
    ),
    ...(activeTask?.status === "blocked"
      ? [`active-task: ${activeTask.label}`]
      : []),
  ];

  return {
    schemaVersion: STUDIO_AGENT_WORKSPACE_MODEL_SCHEMA_VERSION,
    status: blockers.length > 0 ? "warn" : "ready",
    title: input.title ?? "Studio Agent Workspace",
    activeAnchor,
    activeRequirement,
    activeTask,
    collaborators,
    evidenceLanes,
    documentSurfacePolicy,
    chartIntegrationPlan,
    reportWorkspace: input.reportWorkspace ?? undefined,
    ontologyGeneratedApplication:
      input.ontologyGeneratedApplication ?? undefined,
    blockers,
  };
}
